import axios from 'axios';
import { feathers } from '@/feathers-client';
import formatContactName from '@/Core/filters/formatContactName';
import { HIDE_ERROR_AFTER_MS } from '@/Core/utilities';
import { createTagsString } from '../utilities';

const ContactCacheTagDelimiter = '^';
const ContactTagDelimiter = ',';

const LW_TRANSACTION_ZIPFORM = 'LoneWolf Transaction zipForm';

const initialState = {
  bulkActionError: null,
  inProgress: false,
  actionsPerformed: 0,
  errorContacts: [],
  successMessage: null,
  startCampaignSuccess: null,
  deleteContactSuccess: null,
  deleteContactError: null,
  campaignHasRecurringAction: false,
  startCampaignError: null,
  succeededContactIds: [],
  bulkUpdateContactErrorMessage: null,
  bulkUpdateContactError: false,
  updateContactAssigneeErrorMessage: null,
  isContactAssigneeError: false,
  notifyAssigneeErrorMessage: null,
  isNotifyAssigneeError: false,
  bulkActionType: '',
  isStaticListLoading: false,
};

export default {
  namespaced: true,
  state: { ...initialState },
  mutations: {
    setBulkActionType(state, action) {
      switch (action.resource) {
        case 'campaign':
          state.bulkActionType = action.type === 'stop' ? 'Cancel Campaign' : 'Start Campaign';
          break;
        case 'events':
          state.bulkActionType = 'Add Activities';
          break;
        case 'tasks':
          state.bulkActionType = 'Add Tasks';
          break;
        default:
          state.bulkActionType = 'Process';
      }
      if (action.type === 'delete') {
        state.bulkActionType = 'Delete Contacts';
        return;
      }
      if (action.resource === 'contacts' && action.payload && action.payload.hasOwnProperty('tags')) {
        const bulkActionTags = {
          add: 'Add tags',
          replace: 'Replace tags',
          remove: 'Remove tags',
          delete: 'Delete all tags',
        }
        state.bulkActionType = bulkActionTags[action.payload.action];
        return;
      }
      if (action.resource === 'contacts') {
        const bulkActionTypes = {
          hotness_id: 'Edit Hotness',
          source_id: 'Edit Source',
          status: 'Edit Status',
          type_id: 'Edit Contact Type',
          assigned_user_id: 'Edit Assigned User',
        };
        state.bulkActionType = bulkActionTypes[Object.keys(action.payload)[0]];
      }
    },
    setNotifyAssigneeError(state, error) {
      state.notifyAssigneeErrorMessage = error;
      state.isNotifyAssigneeError = true;
    },
    resetNotifyAssigneeError(state) {
      state.isNotifyAssigneeError = false;
      state.notifyAssigneeErrorMessage = null;
    },
    resetSucceededContactIds(state, id) {
      state.succeededContactIds = [];
    },
    addSucceededContactIds(state, id) {
      state.succeededContactIds.push(id);
    },
    setInProgress(state, status) {
      state.inProgress = status;
      if (status === false) {
        state.actionsPerformed = 0;
        state.errorContacts = [];
      }
    },
    successfullAction(state) {
      state.actionsPerformed++;
    },
    errorAction(state, id) {
      state.actionsPerformed++;
      state.errorContacts = [...state.errorContacts, id];
    },
    setUpdateContactAssigneeErrorMessage(state, error) {
      state.updateContactAssigneeErrorMessage = error;
      state.isContactAssigneeError = true;
    },
    resetContactAssigneeError(state) {
      state.isContactAssigneeError = false;
      state.updateContactAssigneeErrorMessage = null;
    },
    setUpdateBulkContactErrorMessage(state, error) {
      state.bulkUpdateContactErrorMessage = error;
      state.bulkUpdateContactError = true;
    },
    resetBulkUpdateContactErrors(state) {
      state.bulkUpdateContactError = false;
      state.bulkUpdateContactErrorMessage = null;
    },
    setStartCampaignSuccess(state, campaignSuccessDetails) {
      if (campaignSuccessDetails && campaignSuccessDetails.numContacts) {
        const { numContacts, campaignName } = campaignSuccessDetails;
        if (numContacts === 1) {
          state.startCampaignSuccess = `Successfully started campaign ${campaignName} for this contact`;
        } else {
          state.startCampaignSuccess = `Successfully started campaign ${campaignName} for ${numContacts} contacts`;
        }
        // if campaignSuccessDetails is falsey, clear the success message
      } else {
        state.startCampaignSuccess = null;
      }
    },
    setDeleteContactSuccess(state, contactSuccessDetails) {
      if (contactSuccessDetails) {
        const { numContacts } = contactSuccessDetails;
        if (numContacts === 1) {
          const { contactName } = contactSuccessDetails;
          state.deleteContactSuccess = `Successfully deleted ${contactName}`;
        } else {
          state.deleteContactSuccess = `Successfully deleted ${numContacts} contacts`;
        }
        // if contactSuccessDetails is falsey then we clear success message
      } else {
        state.deleteContactSuccess = null;
      }
    },
    clearAlerts(state) {
      state.bulkActionError = null;
      state.startCampaignSuccess = null;
      state.startCampaignError = null;
      state.deleteContactSuccess = null;
      state.deleteContactError = null;
      state.successMessage = null;
    },
    bulkActionCompleted(state, { resource, action, total }) {
      state.successMessage =
        total === undefined
          ? `Successfully ${action} ${resource}`
          : `Successfully ${action} ${total} ${resource}`;
    },
    setBulkActionError(state, error) {
      state.bulkActionError = error;
    },
    campaignHasRecurringAction(state, status) {
      state.campaignHasRecurringAction = status;
    },
    hideCompleteMessage(state) {
      state.successMessage = null;
    },
    setStartCampaignError(state, payload) {
      state.startCampaignError = payload;
    },
    setIsStaticListLoading(state, value) {
      state.isStaticListLoading = value;
    },
  },

  actions: {
    async patchTags({ state, commit }, { tags, selected: selectedContacts, removeExistingTags }) {
      await Promise.all(
        selectedContacts.map(async contact => {
          // Supports object model from contact profile and contact table
          const propertyContactTags = contact.tags || contact.tag_names || '';
          const contactTags =
            propertyContactTags.value === undefined
              ? propertyContactTags || ''
              : propertyContactTags.value || '';
          const id = contact._key || contact.id;
          const delimiter = contact.tag_names ? ContactCacheTagDelimiter : ContactTagDelimiter;
          const existingTags = contactTags ? contactTags.split(delimiter).filter(v => !!v) : [];
          let patchTags = tags;
          if (!removeExistingTags) {
            patchTags = existingTags.concat(patchTags);
          }

          // create a unique set of tags (no duplicates)
          patchTags = patchTags.filter((v, i, a) => a.indexOf(v) === i);

          const patchFields = {
            tags: createTagsString(patchTags),
          };
          await feathers
            .service('contacts')
            .patch(id, patchFields)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );
      commit('setInProgress', false);
      commit('bulkActionCompleted', {
        resource: 'contacts',
        action: 'updated',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
    },
    async patchSources({ commit, state }, payload) {
      const { source, selected: selectedContacts } = payload;

      const patchFields = {
        source_id: source.id,
      };

      await Promise.all(
        selectedContacts.map(async id => {
          await feathers
            .service('contacts')
            .patch(id, patchFields)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );
      commit('setInProgress', false);
      commit('bulkActionCompleted', {
        resource: 'sources',
        action: 'updated',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
    },
    async patchStatuses({ commit, state }, payload) {
      const { status, selected: selectedContacts } = payload;

      const patchFields = {
        status: status.name,
      };

      // special case where to delete a status you pass null
      if (status.id === 0) {
        patchFields.status = null;
      }

      await Promise.all(
        selectedContacts.map(async id => {
          await feathers
            .service('contacts')
            .patch(id, patchFields)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );
      commit('bulkActionCompleted', {
        resource: 'statuses',
        action: 'updated',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setInProgress', false);
    },
    async patchHotness({ commit, state }, payload) {
      const { hotness, selected: selectedContacts } = payload;

      const patchFields = {
        hotness_id: hotness,
      };

      await Promise.all(
        selectedContacts.map(async id => {
          await feathers
            .service('contacts')
            .patch(id, patchFields)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );
      commit('bulkActionCompleted', {
        resource: 'hotnesses',
        action: 'updated',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setInProgress', false);
    },
    async patchAssignedUser({ commit, state }, payload) {
      const { assignedUser, selected: selectedContacts } = payload;

      const patchFields = {
        assigned_user_id: assignedUser,
        is_bulk: true,
      };
      // filter contacts where current assigned user is the same that the selected user.
      const filteredContacts = selectedContacts.filter(contact => contact.assigned_user_id !== assignedUser);
      if (filteredContacts.length === 0) {
        const errorMessage =
          'selected assigned user is the current assigned user for all selected contacts';
        commit('setUpdateContactAssigneeErrorMessage', errorMessage);
        commit('setInProgress', false);
        return errorMessage;
      }
      await Promise.all(
        filteredContacts.map(async contact => {
          let contactId = contact.id || contact;
          await feathers
            .service('contacts')
            .patch(contactId, patchFields)
            .then(() => {
              commit('successfullAction');
              commit('addSucceededContactIds', contactId);
            })
            .catch(error => {
              commit('errorAction', contactId);
              commit('setUpdateBulkContactErrorMessage', error.message);
              return error;
            });
        }),
      );

      // if for some reason the update failed for all contacts, don't send email notification.
      const allContactsFailed = state.errorContacts.length === filteredContacts.length;
      if (state.bulkUpdateContactError && allContactsFailed) {
        commit('setInProgress', false);
        return;
      }

      const schedulePayload = {
        contact_assignees: [
          {
            assignee_id: assignedUser,
            contact_ids: state.succeededContactIds,
          },
        ],
      };

      try {
        await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/notify_contact_assignee`,
          schedulePayload,
        );
      } catch (error) {
        commit('resetSucceededContactIds');
        commit('setNotifyAssigneeError', error.message);
        return error;
      }

      commit('resetSucceededContactIds');
      commit('bulkActionCompleted', {
        resource: 'assigned users',
        action: 'updated',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setInProgress', false);
    },
    async patchContactType({ commit, state }, payload) {
      const { contactType, selected: selectedContacts } = payload;

      const patchFields = {
        type_id: contactType,
      };

      await Promise.all(
        selectedContacts.map(async id => {
          await feathers
            .service('contacts')
            .patch(id, patchFields)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );
      commit('bulkActionCompleted', {
        resource: 'contact types',
        action: 'updated',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setInProgress', false);
    },
    async processBulkAction({ commit, rootState, rootGetters }, payload) {
      const contactsTable = rootState.contactsTable;
      commit('setBulkActionType', payload.action);
      commit('setInProgress', true);
      if (contactsTable.selectedActionType === 'all' && !payload.skipQueryBuild) {
        const staticListId = contactsTable.selectedEmailListId;
        if (staticListId != null) {
          payload.list_id = staticListId;
        } else {
          payload.query = rootGetters['contactsTable/queryParamsForAllContacts'];
        }
        if (contactsTable.excludedContactsIds.length > 0 ) {
          payload.excluded_ids = [...contactsTable.excludedContactsIds];
        }
      } else {
        payload.ids = payload.selected;
      }
      delete payload.skipQueryBuild;
      delete payload.selected;
      try {
        await axios({
          url: `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/bulk`,
          method: 'POST',
          data: payload,
        });
      } catch (error) {
        commit('setInProgress', false);
        commit('setUpdateBulkContactErrorMessage', error.data.error);
        return error;
      }

      commit('bulkActionCompleted', {
        resource: payload.action.resource,
        action: payload.action.type,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setInProgress', false);
    },
    async processBulkActionFollowup({ commit }, { payload, masterId }) {
      commit('setBulkActionType', payload.action);
      commit('setInProgress', true);

      delete payload.skipQueryBuild;
      delete payload.selected;

      try {
        await axios({
          url: `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/bulk/follow-up/${masterId}`,
          method: 'POST',
          data: payload,
        });

        commit('bulkActionCompleted', {
          resource: payload.action.resource,
          action: payload.action.type,
        });
        setTimeout(() => {
          commit('hideCompleteMessage');
        }, HIDE_ERROR_AFTER_MS);
      } catch (error) {
        commit('setUpdateBulkContactErrorMessage', error.data.error);
      } finally {
        commit('setInProgress', false);
      }
    },
    // update contact's tags for now, this will be expanded
    async bulkPatchContacts({ dispatch, commit }, payload) {
      commit('setInProgress', true);
      if (payload.tags) {
        await dispatch('patchTags', payload);
      }

      if (payload.source) {
        await dispatch('patchSources', payload);
      }

      if (payload.status) {
        await dispatch('patchStatuses', payload);
      }

      if (payload.hotness || payload.hotness == 0) {
        await dispatch('patchHotness', payload);
      }

      if (payload.assignedUser || payload.assignedUser == 0) {
        await dispatch('patchAssignedUser', payload);
      }

      if (payload.contactType || payload.contactType == 0) {
        await dispatch('patchContactType', payload);
      }
    },
    async bulkCreateEvents(
      { state, commit, dispatch },
      { selectedContacts, type, description, zipformLink },
    ) {
      commit('setInProgress', true);
      commit('clearAlerts');
      await Promise.all(
        selectedContacts.map(async id => {
          const createData = {
            contact_id: id,
            type: type,
            description: description,
          };
          if (type === LW_TRANSACTION_ZIPFORM) {
            createData.site_domain = zipformLink;
          }
          await feathers
            .service('events')
            .create(createData)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );

      dispatch('contactsTable/setShouldReloadContactsTable', null, { root: true });

      commit('bulkActionCompleted', {
        resource: 'events',
        action: 'created',
        total: selectedContacts.length - state.errorContacts.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setInProgress', false);
    },

    /**
     * Starts a campaign on an array of contacts
     *
     * @param {Object} payload
     * @param {Object} payload.selectedContacts The contacts we want to start the campaign
     * @param {Object} payload.campaign The campaign object we want to start
     * @returns {Boolean} Returns True if the request is completed successfully
     */
    async bulkStartCampaigns(
      { dispatch, state, rootState, commit },
      { selectedContacts, campaign },
    ) {
      commit('campaignHasRecurringAction', false);
      commit('setStartCampaignError', null);
      commit('setInProgress', true);
      commit('setStartCampaignSuccess', null);
      let successStarted = selectedContacts.length;
      await Promise.all(
        selectedContacts.map(async id => {
          await feathers
            .service(`campaigns/${campaign.id}/contacts`)
            .create({ contact_id: id })
            .catch(error => {
              commit('errorAction', id);
              if (error.message.match(/Recurring/)) {
                commit('campaignHasRecurringAction', true);
              } else if (error.message.match(campaign.id)) {
                const numContacts =
                  selectedContacts.length === 1 ? 'this contact' : 'one or more contacts';
                const message = `${campaign.name} has already started on ${numContacts} - and cannot be started again`;
                commit('setStartCampaignError', message);
              } else {
                commit('setStartCampaignError', error.message);
              }
              successStarted--;
              return error;
            });
          commit('successfullAction');
        }),
      );
      if (successStarted === selectedContacts.length) {
        commit('setStartCampaignSuccess', {
          numContacts: selectedContacts.length - state.errorContacts.length,
          campaignName: campaign.name,
        });
      }

      commit('setInProgress', false);
    },
    setCampaignHasRecurringAction({ commit }, { status }) {
      commit('campaignHasRecurringAction', status);
    },
    async deleteContacts({ commit, state }, { selectedContacts }) {
      commit('setInProgress', true);
      /*  TODO MM - There is no setDeleteContactError mutation.
        commented temporarily to avoid console error */
      // commit('setDeleteContactError', null);
      await Promise.all(
        selectedContacts.map(async contact => {
          const id = contact.id || contact._key;
          await feathers
            .service('contacts')
            .remove(id)
            .catch(error => {
              commit('errorAction', id);
              return error;
            });
          commit('successfullAction');
        }),
      );
      if (selectedContacts.length === 1 && !state.errorContacts.length) {
        const contact = selectedContacts[0];
        const contactName = formatContactName(contact);
        commit('setDeleteContactSuccess', {
          numContacts: selectedContacts.length,
          contactName,
        });
      } else {
        commit('bulkActionCompleted', {
          resource: 'contacts',
          action: 'deleted',
          total: selectedContacts.length - state.errorContacts.length,
        });
        setTimeout(() => {
          commit('hideCompleteMessage');
        }, HIDE_ERROR_AFTER_MS);
      }
      commit('setInProgress', false);
    },
    clearAlerts({ commit }) {
      commit('clearAlerts');
    },
    async addContactsToStaticList({ commit }, { listId, contactsIds }) {
      commit('setIsStaticListLoading', true);
      await feathers
        .service(`static-lists/${listId}/contacts`)
        .create({ contacts_list: contactsIds });
      commit('bulkActionCompleted', {
        resource: contactsIds.length > 1 ? 'contacts' : 'contact',
        action: 'added',
        total: contactsIds.length,
      });
      setTimeout(() => {
        commit('hideCompleteMessage');
      }, HIDE_ERROR_AFTER_MS);
      commit('setIsStaticListLoading', false);
    },
    resetBulkUpdateContactErrors({ commit }) {
      commit('resetBulkUpdateContactErrors');
    },
    resetContactAssigneeError({ commit }) {
      commit('resetContactAssigneeError');
    },
    resetNotifyAssigneeError({ commit }) {
      commit('resetNotifyAssigneeError');
    },
    setBulkActionType({ commit }, action) {
      commit('setBulkActionType', action);
    },
  },
};
